import React, { useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Check, ChevronDown, ChevronRight, Send } from 'lucide-react';

function normalizeCourseTitle(value = '') {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function normalizeProgrammeCode(value = '') {
  return String(value || '').trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
}

function buildCompletedCourseKeys(completedCourseRecords = []) {
  const keys = new Set();
  completedCourseRecords.forEach((course) => {
    const record = typeof course === 'string' ? { title: course } : (course || {});
    const title = record.title || record.courseTitle || record.programmeTitle || record.name || record.label || '';
    const code = record.programmeCode || record.code || record.acronym || '';
    const normalizedTitle = normalizeCourseTitle(title);
    const normalizedCode = normalizeProgrammeCode(code);
    if (normalizedTitle) keys.add(`title:${normalizedTitle}`);
    if (normalizedCode) keys.add(`code:${normalizedCode}`);
  });
  return keys;
}

function PathwayRecommendationPanel({
  pathways,
  selectedPathwayIds,
  completedCourseRecords = [],
  chatMessages,
  chatInput,
  isChatLoading,
  models,
  selectedModel,
  reasoningEffort,
  reviewIssues = [],
  onSelectModel,
  onChatInputChange,
  onChatSubmit,
  onChatStarter,
  onTogglePathway
}) {
  const chatStreamRef = useRef(null);
  const textareaRef = useRef(null);
  const modelMenuRef = useRef(null);
  const [modelMenuOpen, setModelMenuOpen] = useState(false);
  const [hoveredReasoningModel, setHoveredReasoningModel] = useState('');
  const [openPathwayIds, setOpenPathwayIds] = useState([]);

  useEffect(() => {
    setOpenPathwayIds([]);
  }, [pathways]);

  useEffect(() => {
    if (chatStreamRef.current) {
      chatStreamRef.current.scrollTop = chatStreamRef.current.scrollHeight;
    }
  }, [chatMessages, isChatLoading]);

  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.style.height = 'auto';
    const maxHeight = 24 * 4;
    textarea.style.height = `${Math.min(textarea.scrollHeight, maxHeight)}px`;
  }, [chatInput]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!modelMenuRef.current || modelMenuRef.current.contains(event.target)) return;
      setModelMenuOpen(false);
      setHoveredReasoningModel('');
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const selectedModelDetails = models.find((item) => item.id === selectedModel);
  const modelButtonLabel = selectedModelDetails
    ? `${selectedModelDetails.label}${reasoningEffort ? ` / ${reasoningEffort}` : ''}`
    : 'Select model';
  const completedCourseKeys = buildCompletedCourseKeys(completedCourseRecords);

  const selectModel = (model, effort = '') => {
    onSelectModel(model, effort);
    setModelMenuOpen(false);
    setHoveredReasoningModel('');
  };

  const togglePathwayOpen = (pathwayId) => {
    setOpenPathwayIds((currentIds) => (
      currentIds.includes(pathwayId)
        ? currentIds.filter((id) => id !== pathwayId)
        : [...currentIds, pathwayId]
    ));
  };

  const metricTone = (value = '') => {
    const normalized = String(value).toLowerCase();
    if (normalized.includes('high') || normalized.includes('strong')) return 'high';
    if (normalized.includes('medium') || normalized.includes('moderate')) return 'med';
    return 'low';
  };

  return (
    <div className="pathway-recommendation-panel">
      {completedCourseRecords.length > 0 && (
        <div className="recommendation-section-header recommendation-section-header--stacked">
          <div className="section-label">Already Taken at LBS</div>
          <div className="participant-taken-row">
            {completedCourseRecords.map((course) => (
              <span key={`taken-card-${course.title}`} className="tag format">
                {course.title}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="pathway-recommendation-layout">
        <section className="pathway-adviser-panel">
          <div className="pathway-chat-card chat-card">
            <div className="chat-stream schema-scroll" ref={chatStreamRef}>
              {chatMessages.map((message, index) => (
                <div key={`${message.role}-${index}`} className={`chat-message-row ${message.role === 'user' ? 'user' : 'ai'}`}>
                  <div className="message-speaker">{message.role === 'user' ? 'You' : 'Agent'}</div>
                  <div className={`bubble ${message.role === 'user' ? 'user' : 'ai'}`}>
                    {message.role === 'user' ? (
                      <div>{message.content}</div>
                    ) : (
                      <div className="ai-message-text markdown-body">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                          {message.content || ''}
                        </ReactMarkdown>
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {isChatLoading && (
                <div className="chat-message-row ai">
                  <div className="message-speaker">Agent</div>
                  <div className="bubble ai typing-bubble" aria-label="Assistant is typing">
                    <span className="typing-dot" />
                    <span className="typing-dot" />
                    <span className="typing-dot" />
                  </div>
                </div>
              )}
            </div>

            <div className="pathway-chat-starters">
              {[
                'Compare the strongest two options.',
                'Make this more digital.',
                'Which is safest for a senior manager?'
              ].map((starter) => (
                <button key={starter} type="button" onClick={() => onChatStarter(starter)}>
                  {starter}
                </button>
              ))}
            </div>

            <div className="chat-composer-shell">
              <textarea
                ref={textareaRef}
                className="chat-textarea"
                placeholder="Ask anything"
                value={chatInput}
                onChange={(event) => onChatInputChange(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && !event.shiftKey && !isChatLoading) {
                    event.preventDefault();
                    onChatSubmit(event);
                  }
                }}
                rows={1}
                disabled={isChatLoading}
              />
              <div className="chat-composer-toolbar">
                <div className="chat-composer-left">
                  <div className="model-menu" ref={modelMenuRef}>
                    <button
                      type="button"
                      className="model-menu-trigger"
                      onClick={() => setModelMenuOpen((previous) => !previous)}
                      aria-haspopup="menu"
                      aria-expanded={modelMenuOpen}
                    >
                      <span>{modelButtonLabel}</span>
                      <ChevronDown size={14} />
                    </button>
                    {modelMenuOpen && (
                      <div className="model-menu-popover" role="menu">
                        {models.length === 0 && <div className="model-menu-empty">Loading models</div>}
                        {models.map((item) => (
                          <div
                            key={item.id}
                            className="model-menu-row"
                            onMouseEnter={() => setHoveredReasoningModel(item.id)}
                            onFocus={() => setHoveredReasoningModel(item.id)}
                          >
                            <button type="button" onClick={() => selectModel(item)}>
                              <span>{item.label}</span>
                              {selectedModel === item.id && <Check size={13} />}
                            </button>
                            {item.supportsReasoning && (
                              <div className="reasoning-flyout-wrap">
                                <button
                                  type="button"
                                  className="reasoning-flyout-trigger"
                                  aria-label={`Choose reasoning level for ${item.label}`}
                                  onMouseEnter={() => setHoveredReasoningModel(item.id)}
                                  onFocus={() => setHoveredReasoningModel(item.id)}
                                >
                                  <ChevronRight size={14} />
                                </button>
                                {hoveredReasoningModel === item.id && (
                                  <div className="reasoning-flyout">
                                    {item.reasoningEfforts.map((effort) => (
                                      <button
                                        key={`${item.id}-${effort}`}
                                        type="button"
                                        onClick={() => selectModel(item, effort)}
                                        className={selectedModel === item.id && reasoningEffort === effort ? 'active' : ''}
                                      >
                                        {effort}
                                      </button>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  className="composer-send-btn"
                  onClick={onChatSubmit}
                  disabled={isChatLoading || !chatInput.trim()}
                  aria-label="Send"
                >
                  <Send size={18} />
                </button>
              </div>
            </div>
          </div>
        </section>

        <section className="pathway-bundle-panel">
          <div className="pathway-bundle-list">
            {pathways.length === 0 && (
              <div className="pathway-empty-review-card">
                <div className="section-label">No Pathway Cards Returned</div>
                <p>The recommendation workflow produced review issues, but no valid pathway cards that the app can render yet.</p>
                {reviewIssues.length > 0 && (
                  <ul>
                    {reviewIssues.map((issue, index) => (
                      <li key={`review-issue-${index}`}>{issue.message || issue.fixInstruction || issue}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}
            {pathways.map((pathway) => {
              const isSelected = selectedPathwayIds.includes(pathway.id);
              const isOpen = openPathwayIds.includes(pathway.id);
              const fitMetrics = pathway.fitSummary
                ? [
                  ['Objective', pathway.fitSummary.objectiveAlignment],
                  ['Delivery', pathway.fitSummary.deliveryFit],
                  ['Timing', pathway.fitSummary.timingFit],
                  ['Theme', pathway.fitSummary.themeFit]
                ]
                : [];

              return (
                <article key={pathway.id} className={`pathway-bundle-card ${isSelected ? 'highlighted' : ''} ${isOpen ? 'open' : 'closed'}`}>
                  <header
                    className="pathway-bundle-card-head"
                    role="button"
                    tabIndex={0}
                    onClick={() => togglePathwayOpen(pathway.id)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        togglePathwayOpen(pathway.id);
                      }
                    }}
                  >
                    <label className={`pathway-bundle-select-row ${isSelected ? 'selected' : ''}`} onClick={(event) => event.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => onTogglePathway(pathway.id)}
                      />
                      <span className="pathway-checkbox-mark">{isSelected && <Check size={13} />}</span>
                    </label>
                    <div className="pathway-bundle-title-wrap">
                      <div className="pathway-option-title">{pathway.label}</div>
                      {pathway.isPreferred && <span className="pathway-card-tag pathway-card-tag--preferred">Preferred</span>}
                      {!pathway.isPreferred && <span className="pathway-card-tag pathway-card-tag--alternative">Alternative</span>}
                      {pathway.comparisonLabel && <span className="pathway-card-tag">{pathway.comparisonLabel}</span>}
                      {!pathway.comparisonLabel && (pathway.theme || pathway.certification) && <span className="pathway-card-tag">{pathway.theme || pathway.certification}</span>}
                    </div>
                    <span className="pathway-card-toggle" aria-hidden="true">
                      <ChevronDown size={16} className={`pathway-card-chevron ${isOpen ? 'open' : ''}`} />
                    </span>
                  </header>

                  {isOpen && (
                    <div className="pathway-bundle-body">
                      {fitMetrics.length > 0 && (
                        <div className="pathway-fit-chips">
                          {fitMetrics.map(([label, value]) => (
                            <span key={label} className={`pathway-fit-chip pathway-fit-chip--${metricTone(value)}`}>
                              <span>{label}</span>
                              <strong>{value}</strong>
                            </span>
                          ))}
                        </div>
                      )}

                      <p className="pathway-option-summary">{pathway.summary}</p>
                      <ol className="pathway-bundle-programmes">
                        {(pathway.programmes || []).map((programme, index) => {
                          const code = programme.programmeCode || programme.code || '';
                          const isCompleted = Boolean(programme.isCompleted)
                            || completedCourseKeys.has(`code:${normalizeProgrammeCode(code)}`)
                            || completedCourseKeys.has(`title:${normalizeCourseTitle(programme.title)}`);
                          return (
                            <li key={`${pathway.id}-${programme.title}-${index}`} className={`pathway-bundle-programme ${isCompleted ? 'pathway-bundle-programme--completed' : ''}`}>
                              <span className={`pathway-step-chip ${isCompleted ? 'pathway-step-chip--completed' : ''}`}>{isCompleted ? 'Done' : `Step ${index + 1}`}</span>
                              <div>
                                <div className="pathway-programme-heading">
                                  <strong>{code ? `${code}: ${programme.title}` : programme.title}</strong>
                                  {isCompleted && <span className="pathway-completed-badge">Completed</span>}
                                </div>
                                {programme.reason && <p>{programme.reason}</p>}
                              </div>
                            </li>
                          );
                        })}
                      </ol>
                      {pathway.tradeoffs && <p className="pathway-option-tradeoff"><strong>Trade-off:</strong> {pathway.tradeoffs}</p>}
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        </section>
      </div>
    </div>
  );
}

export default PathwayRecommendationPanel;
